// Pure stroke rendering: perfect-freehand outlines filled onto a 2D context.
// Assumes the caller has already set the logical-units transform on the context.
// backend/src/services/drawing/strokeRasterizer.ts ports this for saved images,
// so a change to the perfect-freehand options or path smoothing goes in both.

import { getStroke } from 'perfect-freehand';
import type { Stroke } from './types';

function buildStrokePath(outline: number[][]): Path2D {
  const path = new Path2D();
  if (outline.length === 0) {
    return path;
  }
  path.moveTo(outline[0][0], outline[0][1]);
  // Quadratic curves through midpoints smooth the polygon edges
  for (let i = 1; i < outline.length; i++) {
    const [x0, y0] = outline[i - 1];
    const [x1, y1] = outline[i];
    path.quadraticCurveTo(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
  }
  path.closePath();
  return path;
}

function renderStroke(ctx: CanvasRenderingContext2D, stroke: Stroke): void {
  // Real pen pressure varies; mouse/touch report a constant 0.5, where
  // perfect-freehand's velocity-based simulation looks better
  const hasRealPressure = stroke.points.some(
    ([, , pressure]) => pressure !== 0.5
  );
  const outline = getStroke(stroke.points, {
    size: stroke.thickness,
    thinning: 0.5,
    smoothing: 0.5,
    streamline: 0.5,
    simulatePressure: !hasRealPressure,
  });

  ctx.save();
  ctx.globalAlpha = stroke.opacity;
  ctx.fillStyle = stroke.colorHex;
  ctx.fill(buildStrokePath(outline));
  ctx.restore();
}

export function renderStrokes(
  ctx: CanvasRenderingContext2D,
  strokes: Stroke[]
): void {
  for (const stroke of strokes) {
    renderStroke(ctx, stroke);
  }
}
